// Panel central: el ledger de artículos del Anexo, agrupado por paquete.
//
// Portado de Produccion.jsx + WorkingEncomienda.jsx: el autoguardado SILENCIOSO
// a los 3 s con indicador pasivo (inactivo | guardando | guardado | fallo) y la
// barra de totales pegajosa al pie.
//
// El agregar-artículo ya no vive a nivel de panel: cada paquete tiene el suyo,
// porque un artículo sin paquete no tiene dónde caer (package_id es NOT NULL).
//
// renderPaqueteExtras / renderItemExtras: render-props opcionales que se
// pasan tal cual a PackageGroup (default null → salida idéntica a la previa).
//
// alturaAuto (default false → HTML idéntico): el panel toma su altura natural,
// sin tope ni scroll interno; scrollea el contenedor de la pestaña. La barra de
// totales (ya sticky bottom-0) queda pegada al pie de ESE scroll, y el
// encabezado de columnas de cada paquete se fija arriba mientras se recorre.

import { Plus } from 'lucide-react';
import PackageGroup from './PackageGroup';
import AnexoTotalsBar from './AnexoTotalsBar';
import { aplanarItems, ordenarPaquetes } from './helpers';

const ESTADO_COPY = {
  inactivo: '',
  guardando: 'Guardando…',
  guardado: 'Guardado',
  fallo: 'No se pudo guardar',
};

const ProduccionPanel = ({
  paquetes,
  estadoGuardado,
  ultimoAgregadoId,
  ultimoPaqueteId,
  onChangeNombrePaquete,
  onChangeItem,
  onQuitarItem,
  onAgregarItem,
  onAgregarPaquete,
  renderPaqueteExtras = null,
  renderItemExtras = null,
  alturaAuto = false,
}) => {
  const ordenados = ordenarPaquetes(paquetes);
  // Totales: se aplanan los artículos de todos los paquetes en un solo paso.
  const todos = aplanarItems(ordenados);

  return (
    <div
      className={
        alturaAuto
          ? 'flex flex-col rounded-lg border border-slate-200 bg-white'
          : 'flex h-full min-h-0 flex-col rounded-lg border border-slate-200 bg-white'
      }
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
        <h3 className="text-sm font-bold text-slate-900">Artículos del Anexo</h3>
        <span
          role="status"
          className={`text-[11px] ${estadoGuardado === 'fallo' ? 'text-red-600' : 'text-slate-400'}`}
        >
          {ESTADO_COPY[estadoGuardado] || ''}
        </span>
      </div>

      <div className={alturaAuto ? 'p-2' : 'min-h-0 flex-1 overflow-y-auto p-2'}>
        {ordenados.length === 0 ? (
          <div className="px-2 py-10 text-center">
            <p className="text-xs text-slate-400">Empezá creando el primer paquete.</p>
            <button
              type="button"
              onClick={onAgregarPaquete}
              className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-4 text-sm font-medium text-slate-500 hover:bg-slate-50"
            >
              <Plus size={15} aria-hidden="true" /> Nuevo paquete
            </button>
          </div>
        ) : (
          <>
            {ordenados.map((pkg) => (
              <PackageGroup
                key={pkg.id}
                paquete={pkg}
                items={pkg.items || []}
                autoFocusNombre={pkg.id === ultimoPaqueteId}
                ultimoAgregadoId={ultimoAgregadoId}
                onChangeNombre={onChangeNombrePaquete}
                onChangeItem={onChangeItem}
                onQuitarItem={onQuitarItem}
                onAgregarItem={onAgregarItem}
                renderPaqueteExtras={renderPaqueteExtras}
                renderItemExtras={renderItemExtras}
                encabezadoFijo={alturaAuto}
              />
            ))}

            <button
              type="button"
              onClick={onAgregarPaquete}
              className="mt-1 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-4 text-sm font-medium text-slate-500 hover:bg-slate-50"
            >
              <Plus size={15} aria-hidden="true" /> Nuevo paquete
            </button>
          </>
        )}
      </div>

      {/* Totales: suma plana de TODOS los artículos, sin importar el paquete. */}
      <AnexoTotalsBar items={todos} />
    </div>
  );
};

export default ProduccionPanel;
